import * as React from 'react';

// API
import apibridge from '../../../apibridge';

// UTILS
import { validationErrorsToString } from '../../../utils';
import { useUpdateTotalNotification } from '../../../utils/useUpdateTotalNotification';

// REDUX
import { useAppSelector } from '../../../store';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../../components/Button';
import LoadingSpinner from '../../../components/LoadingSpinner';

// TYPES
import { ComplaintResolutionCommand } from '../../../api/models';
interface ResolveModalTypes {
	complaintId?: string;
}

const ResolveModal: React.FC<ResolveModalTypes> = ({ complaintId }) => {
	const history = useHistory();

	// Get all complaint resolutions from Redux store
	const complaintResolutionStatuses = useAppSelector((state) => state.lists.complaintResolutionStatuses);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const displayLabel = (value?: string) => {
		if (value) {
			if (value === 'InsufficientInformation') {
				return (
					<>
						<strong>Insufficient information — </strong>No further action required
					</>
				);
			} else if (value === 'Withdrawn') {
				return (
					<>
						<strong>Complaint withdrawn by complainant — </strong>No further action required
					</>
				);
			} else if (value === 'NotSubstantiated') {
				return (
					<>
						<strong>Complaint investigated and not substantiated — </strong>Please ensure that the reasons for this
						decision have been recorded.
					</>
				);
			} else if (value === 'Substantiated') {
				return (
					<>
						<strong>Complaint investigated and substantiated — </strong>Please ensure that all of the following
						information has been recorded: authorities notified, the reasons for this decision, the disciplinary action
						taken, communication with parents and child and support provided, and actions taken by the organisation to
						reduce the risk of this occurring again.
					</>
				);
			} else {
				return null;
			}
		}
	};

	// Set up data state and initial data
	const initialFormData: ComplaintResolutionCommand = {
		id: undefined,
		resolution: undefined,
		notes: ''
	};

	const [complaintsResolveFormData, setComplaintsResolveFormData] = React.useState(initialFormData);

	const resolveComplaint = async (values: any) => {
		setIsLoading(true);

		const data: ComplaintResolutionCommand = {
			id: complaintId,
			resolution: values.resolutionInput,
			notes: values.notesInput
		};

		setComplaintsResolveFormData(data);

		const response = await apibridge.resolveComplaint(data);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			history.push('/complaints');

			useUpdateTotalNotification();
		}
	};

	// Set up Formik
	const validationSchemaStepOne = Yup.object({
		resolutionInput: Yup.string().required('Required'),
		notesInput: Yup.string().required('Required')
	});

	const initialValuesStepOne = {
		resolutionInput: complaintsResolveFormData.resolution,
		notesInput: complaintsResolveFormData.notes
	};

	return (
		<>
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<Formik
									initialValues={initialValuesStepOne}
									validationSchema={validationSchemaStepOne}
									onSubmit={resolveComplaint}
									enableReinitialize
								>
									{(formik) => {
										return (
											<Form translate="yes" className="general-form">
												<div className="form-group">
													<label className="label mbtom-14">State type of resolution</label>
													<div className="form-field">
														{complaintResolutionStatuses &&
															complaintResolutionStatuses.length > 0 &&
															complaintResolutionStatuses.map((item, index) => {
																return (
																	<div className="radio-row align-start mbtom-20" key={index}>
																		<Field
																			name="resolutionInput"
																			type="radio"
																			value={item.value}
																			checked={formik.values.resolutionInput === item.value}
																			onChange={() => formik.setFieldValue('resolutionInput', item.value)}
																		/>

																		<span className="radio-option">{displayLabel(item.value)}</span>
																	</div>
																);
															})}

														<ErrorMessage name="resolutionInput" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-group">
													<label htmlFor="notesInput" className="label">
														Notes
													</label>
													<div className="form-field">
														<Field as="textarea" name="notesInput" id="notesInput" type="text" className="form-input" />
														<ErrorMessage name="notesInput" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-button-group">
													<Button type="submit" className="btn-primary w-100">
														Submit
													</Button>
												</div>
											</Form>
										);
									}}
								</Formik>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</>
	);
};

export default ResolveModal;
